import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Sparkles,
  MoreVertical,
  Link2Off,
  Plus,
  Ban,
  RotateCcw,
  Search,
  ArrowRightLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BankTransaction } from '@/hooks/useBankTransactions';
import { SystemPayment } from '@/hooks/useSystemPayments';
import { ReasonConfirmDialog } from '@/components/ui/reason-confirm-dialog';

interface BankTransactionCardProps {
  transaction: BankTransaction;
  suggestedMatch: SystemPayment | null;
  onReconcile: () => void;
  onIgnore: (reason: string) => void;
  onUnlink: (reason: string) => void;
  onCreateTransaction: () => void;
  onSearchReceivables?: () => void;
  onCreateTransfer?: () => void;
  isIgnored?: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Math.abs(value));
};

export const BankTransactionCard = ({
  transaction,
  suggestedMatch,
  onReconcile,
  onIgnore,
  onUnlink,
  onCreateTransaction,
  onSearchReceivables,
  onCreateTransfer,
  isIgnored = false,
}: BankTransactionCardProps) => {
  const [showIgnoreDialog, setShowIgnoreDialog] = useState(false);
  const [ignoreReason, setIgnoreReason] = useState('');
  // ESTORNO-AUDITORIA (I.160, E9): "Restaurar" vira "Desfazer conciliação", com confirmação e motivo
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);

  const isCredit = transaction.transaction_type === 'credit';

  const handleIgnore = () => {
    onIgnore(ignoreReason);
    setShowIgnoreDialog(false);
    setIgnoreReason('');
  };

  return (
    <>
      <div className="p-4 hover:bg-muted/50 transition-colors">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`p-2 rounded-full ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
              {isCredit ? (
                <ArrowDownCircle className="h-4 w-4 text-green-600" />
              ) : (
                <ArrowUpCircle className="h-4 w-4 text-red-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 mb-1">
                <span className="text-sm text-muted-foreground shrink-0">
                  {format(new Date(`${transaction.transaction_date}T12:00:00`), 'dd/MM', { locale: ptBR })}
                </span>
                <span className="font-medium line-clamp-2 break-words min-w-0" title={transaction.description}>{transaction.description}</span>
              </div>
              {transaction.counterpart_name && (
                <p className="text-sm text-muted-foreground truncate" title={transaction.counterpart_name}>
                  {transaction.counterpart_name}
                </p>
              )}
              {isIgnored && transaction.ignore_reason && (
                <p className="text-xs text-muted-foreground mt-1 italic">
                  Motivo: {transaction.ignore_reason}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`font-bold whitespace-nowrap tabular-nums ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
              {isCredit ? '+' : '-'} {formatCurrency(transaction.value)}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!isIgnored ? (
                  <>
                    {onSearchReceivables && (
                      <DropdownMenuItem onClick={onSearchReceivables}>
                        <Search className="h-4 w-4 mr-2" />
                        Buscar Lançamento
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={onCreateTransaction}>
                      <Plus className="h-4 w-4 mr-2" />
                      Criar Lançamento
                    </DropdownMenuItem>
                    {onCreateTransfer && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={onCreateTransfer}>
                          <ArrowRightLeft className="h-4 w-4 mr-2" />
                          Transferência Entre Contas
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowIgnoreDialog(true)}>
                      <Ban className="h-4 w-4 mr-2" />
                      Ignorar
                    </DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onClick={() => setShowUnlinkDialog(true)}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Desfazer conciliação
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Suggested Match */}
        {suggestedMatch && !isIgnored && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-4 w-4 text-green-600 shrink-0" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400 break-words min-w-0">
                  Encontramos uma correspondência!
                </span>
              </div>
              <Button size="sm" variant="default" onClick={onReconcile} className="bg-green-600 hover:bg-green-700 shrink-0 whitespace-nowrap ml-auto">
                Conciliar
              </Button>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">
              <p className="font-medium line-clamp-2 break-words" title={suggestedMatch.description}>{suggestedMatch.description}</p>
              <p className="text-xs">
                {suggestedMatch.customer_name || suggestedMatch.supplier_name} • {formatCurrency(suggestedMatch.payment_value)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Ignore Dialog */}
      <Dialog open={showIgnoreDialog} onOpenChange={setShowIgnoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ignorar Transação</DialogTitle>
            <DialogDescription>
              Informe o motivo para ignorar esta transação bancária.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{transaction.description}</p>
              <p className={`text-lg font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                {isCredit ? '+' : '-'} {formatCurrency(transaction.value)}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Motivo</Label>
              <Textarea
                id="reason"
                placeholder="Ex: Transferência entre contas, estorno já contabilizado..."
                value={ignoreReason}
                onChange={(e) => setIgnoreReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIgnoreDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleIgnore} disabled={!ignoreReason.trim()}>
              Ignorar Transação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ESTORNO-AUDITORIA (I.160, E9): desfazer conciliação com motivo */}
      <ReasonConfirmDialog
        open={showUnlinkDialog}
        onOpenChange={setShowUnlinkDialog}
        title="Desfazer conciliação"
        description="A linha volta a pendente e o motivo do 'Ignorar' fica guardado no registro; se houver baixa ligada, ela é estornada e guardada no histórico de estornos, com você, a data e o motivo."
        reasonLabel="Motivo"
        confirmText="Desfazer conciliação"
        onConfirm={(reason) => onUnlink(reason)}
      />
    </>
  );
};
